import { shortHumanizer } from '@/utils';
import { format } from 'date-fns';
import { atom } from 'jotai';

export const stateAtom = atom({
  sorting: [{ id: 'startTime', desc: false }],
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
  query: {},
  parameters: {},
});

export const pipelineFn = (
  pagination,
  query,
  sorting,
  categoryTypes = [],
  attributeTypes = [],
) =>
  [
    { $match: query },
    {
      $project: {
        _id: false,
        identifier: true,
        startTime: true,
        endTime: true,
        objective: true,
        durationSeconds: true,
        person: {
          code: true,
          forenames: true,
          surname: true,
          role: true,
          collarNumber: true,
          rank: { code: true },
          radioSsi: true,
          groups: categoryTypes.reduce(
            (acc, value) => ({
              ...acc,
              [value]: true,
            }),
            {},
          ),
          attributes: attributeTypes.reduce(
            (acc, value) => ({
              ...acc,
              [value]: true,
            }),
            {},
          ),
        },
        compliant: true,
      },
    },
    sorting.length > 0 && {
      $sort: sorting.reduce(
        (acc, { id, desc }) => ({ ...acc, [id]: desc ? -1 : 1 }),
        {},
      ),
    },
    { $skip: pagination.pageIndex * pagination.pageSize },
    { $limit: pagination.pageSize },
  ].filter(Boolean);

export const totalsPipelineFn = (query) => [
  { $match: query },
  {
    $group: {
      _id: null,
      total: { $sum: 1 },
      durationSeconds: { $sum: '$durationSeconds' },
    },
  },
  { $unset: '_id' },
];

export const downloadPipelineFn =
  (collection, categoryTypes = [], attributeTypes = []) =>
  (query) => [
    { $match: query },
    {
      $project: {
        _id: false,
        identifier: true,
        startTime: true,
        endTime: true,
        objective: true,
        durationSeconds: true,
        person: {
          code: true,
          forenames: true,
          surname: true,
          role: true,
          collarNumber: true,
          rank: { code: true },
          radioSsi: true,
          groups: categoryTypes.reduce(
            (acc, value) => ({
              ...acc,
              [value]: true,
            }),
            {},
          ),
          attributes: attributeTypes.reduce(
            (acc, value) => ({
              ...acc,
              [value]: true,
            }),
            {},
          ),
        },
        compliant: true,
        link: {
          $concat: [
            `${window.location.origin}/eventreplay/${collection}/`,
            '$identifier',
          ],
        },
      },
    },
  ];

export const columnsFn = (footer, categories = [], attributes = []) => [
  {
    header: 'Staff ID',
    id: 'person.code',
    accessorFn: ({ person: { code } = {} } = {}) => code ?? '',
  },
  {
    header: 'Collar Number',
    id: 'person.collarNumber',
    accessorFn: ({ person: { collarNumber } = {} } = {}) => collarNumber ?? '',
  },
  {
    header: 'Forenames',
    id: 'person.forenames',
    accessorFn: ({ person: { forenames } = {} } = {}) => forenames ?? '',
  },
  {
    header: 'Surname',
    id: 'person.surname',
    accessorFn: ({ person: { surname } = {} } = {}) => surname ?? '',
  },
  {
    header: 'SSI',
    id: 'person.radioSsi',
    accessorFn: ({ person: { radioSsi } = {} } = {}) => radioSsi ?? '',
  },
  {
    header: 'Role',
    id: 'person.role',
    accessorFn: ({ person: { role } = {} } = {}) => role ?? '',
  },
  {
    header: 'Rank',
    id: 'person.rank.code',
    accessorFn: ({ person: { rank: { code } = {} } = {} } = {}) => code ?? '',
  },
  ...categories.map(({ label, value }) => ({
    header: label,
    id: `person.groups.${value}`,
    accessorFn: ({ person: { groups = {} } = {} } = {}) => groups[value] ?? '',
  })),
  {
    header: 'Objective Identifier',
    id: 'objective.identifier',
    accessorFn: ({ objective: { identifier } = {} } = {}) => identifier ?? '',
  },
  {
    header: 'Objective Title',
    id: 'objective.title',
    accessorFn: ({ objective: { title } = {} } = {}) => title ?? '',
  },
  ...attributes.map(({ label, value }) => ({
    header: label,
    id: `person.attributes.${value}`,
    accessorFn: ({ person: { attributes = {} } = {} } = {}) =>
      attributes[value] ?? '',
  })),
  {
    header: 'Start Time',
    accessorKey: 'startTime',
    Cell: ({ cell }) => format(cell.getValue(), 'dd/MM/yyyy HH:mm:ss'),
  },
  {
    header: 'End Time',
    accessorKey: 'endTime',
    Cell: ({ cell }) => format(cell.getValue(), 'dd/MM/yyyy HH:mm:ss'),
  },
  {
    header: 'Duration',
    accessorKey: 'durationSeconds',
    Cell: ({ cell }) =>
      cell.getValue() ? shortHumanizer(cell.getValue() * 1000) : '',
    footer: footer.durationSeconds
      ? shortHumanizer(footer.durationSeconds * 1000)
      : '',
  },
  {
    header: 'Compliant',
    id: 'compliant',
    accessorFn: ({ compliant }) => (compliant ? 'Yes' : 'No'),
  },
];
